import dayjs from "dayjs";

export const min_to_ms = min => min * 60 * 1000;
export const ms_to_s = ms => ms / 1000;

// The app's one date format, in the UI language: "20 Oct 2026, 22:02".
const DATE_PARTS = { day: "numeric", month: "short", year: "numeric" };
const TIME_PARTS = { hour: "2-digit", minute: "2-digit" };
const DATE_TIME_PRESETS = {
  date: DATE_PARTS,
  month: { month: "short", year: "numeric" },
  time: TIME_PARTS,
  timeSeconds: { ...TIME_PARTS, second: "2-digit" },
  dateTime: { ...DATE_PARTS, ...TIME_PARTS },
  dateTimeSeconds: { ...DATE_PARTS, ...TIME_PARTS, second: "2-digit" },
};

// Full tag of the UI language, set by src/i18n.js; plain "en" would print US order.
let dateTimeLocale = "en-GB";
export const setDateTimeLocale = locale => {
  dateTimeLocale = locale || "en-GB";
};

// One cached formatter per locale and preset; an unknown locale gets the default.
const DATE_FMT_CACHE = {};
const dateFmt = (locale, preset) => {
  const key = `${locale}|${preset}`;
  if (!(key in DATE_FMT_CACHE)) {
    const parts = DATE_TIME_PRESETS[preset];
    try {
      DATE_FMT_CACHE[key] = new Intl.DateTimeFormat(locale, parts);
    } catch (_) {
      DATE_FMT_CACHE[key] = new Intl.DateTimeFormat(undefined, parts);
    }
  }
  return DATE_FMT_CACHE[key];
};

// Takes a dayjs/Date/epoch-ms/ISO input; "" when it is missing or invalid.
const formatAs = (preset, input, locale) => {
  if (input == null || input === "") return "";
  const date = dayjs(input);
  if (!date.isValid()) return "";
  return dateFmt(locale || dateTimeLocale, preset).format(date.toDate());
};

export const formatDateTime = (input, { seconds = false, locale } = {}) =>
  formatAs(seconds ? "dateTimeSeconds" : "dateTime", input, locale);

export const formatDate = (input, { locale } = {}) =>
  formatAs("date", input, locale);

export const formatTime = (input, { seconds = false, locale } = {}) =>
  formatAs(seconds ? "timeSeconds" : "time", input, locale);

export const formatMonth = (input, { locale } = {}) =>
  formatAs("month", input, locale);

const REL_UNITS = [
  ["year", 31536000000],
  ["month", 2592000000],
  ["day", 86400000],
  ["hour", 3600000],
  ["minute", 60000],
  ["second", 1000],
];

// Intl.RelativeTimeFormat binds its locale at construction, so cache one
// formatter per locale. A falsy locale falls back to the runtime default.
const REL_FMT_CACHE = {};
const relFmt = locale => {
  if (typeof Intl === "undefined" || !Intl.RelativeTimeFormat) return null;
  const key = locale || "";
  if (!(key in REL_FMT_CACHE)) {
    try {
      REL_FMT_CACHE[key] = new Intl.RelativeTimeFormat(locale || undefined, {
        numeric: "auto",
      });
    } catch (_) {
      // Invalid/unknown locale string — fall back to no formatter.
      REL_FMT_CACHE[key] = null;
    }
  }
  return REL_FMT_CACHE[key];
};

// Localised "X hours ago" label from a dayjs/Date/epoch-ms/ISO input.
// Pass the app's active language (e.g. getLangFromLocale(i18n.language)) so the
// relative time follows the UI language rather than the browser's locale.
// Returns "" when unavailable so callers can fall back to an absolute date.
export const relativeTimeFromNow = (input, locale) => {
  const fmt = relFmt(locale);
  if (input == null || input === "" || !fmt) return "";
  const ms =
    typeof input === "number"
      ? input
      : typeof input === "string"
        ? new Date(input).getTime()
        : input.valueOf();
  const diff = ms - Date.now();
  if (Number.isNaN(diff)) return "";
  for (const [unit, unitMs] of REL_UNITS) {
    if (Math.abs(diff) >= unitMs || unit === "second") {
      return fmt.format(Math.round(diff / unitMs), unit);
    }
  }
  return "";
};
